import { Button } from "@/components";
import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  DemoFormModal,
  FAQ,
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import chatFidexaImg from "@/assets/chat-fidexa.png";

const SITE_URL = "https://fidexa.com.br";
const PATH = "/crediario";
// Sem o sufixo "| Fidexa" aqui: o <Helmet titleTemplate="%s | Fidexa"> global
// (src/App.tsx) já concatena o sufixo no <title> renderizado. og:title/
// twitter:title não passam pelo titleTemplate, então usam OG_TITLE (com
// sufixo) separadamente.
const TITLE = "Sistema de Crediário para Redes de Lojas"; // ≤ 60 chars com sufixo do template
const OG_TITLE = `${TITLE} | Fidexa`;
const DESCRIPTION =
  "Sistema de crediário próprio para redes de 4 a 20 lojas: cadastro de clientes, controle de parcelas e cobrança automática via WhatsApp em um só painel."; // ~155 chars

const MERRIWEATHER = { fontFamily: "var(--font-merriweather)" } as const;
const WHATSAPP_NUMBER = "5519936195831";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Sistema de Crediário",
      item: `${SITE_URL}${PATH}`,
    },
  ],
};

const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Fidexa — Sistema de Crediário",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Sistema de crediário e cobrança via WhatsApp para o varejo",
  operatingSystem: "Web",
  url: `${SITE_URL}${PATH}`,
  description: DESCRIPTION,
  inLanguage: "pt-BR",
  provider: { "@type": "Organization", name: "Fidexa", url: SITE_URL },
  offers: {
    "@type": "Offer",
    priceCurrency: "BRL",
    price: "0",
    availability: "https://schema.org/InStock",
    description: "Solicite uma demonstração personalizada para sua rede de lojas.",
  },
};

const BENEFITS = [
  "Cadastro completo de clientes, parcelas e histórico de compras do seu crediário próprio.",
  "Controle de inadimplência em tempo real, com visão consolidada por loja e por cliente.",
  "Cobrança automática via WhatsApp integrada ao mesmo painel do sistema de crediário.",
  "Todas as lojas da rede centralizadas em um único acesso, sem planilhas paralelas.",
  "Conformidade com a LGPD e API oficial do WhatsApp (Meta).",
];

const FAQ_ITEMS = [
  {
    question:
      "Preciso trocar de sistema para migrar meu crediário para a Fidexa?",
    answer:
      "Não. Você pode migrar sua base de clientes, parcelas e vencimentos por planilha ou via integração por API com o ERP que sua rede já usa — sem custo adicional. A migração é feita antes de qualquer cobrança começar a ser disparada.",
  },
  {
    question:
      "Consigo gerenciar o crediário de várias lojas em um único painel?",
    answer:
      "Sim. A Fidexa foi feita para redes de 4 a 20 lojas: todas as unidades ficam centralizadas em um único acesso, com visão consolidada por loja e por cliente, e as mesmas regras de crédito padronizadas para toda a rede.",
  },
  {
    question:
      "Como a cobrança automática via WhatsApp se conecta ao sistema de crediário?",
    answer:
      "A cobrança roda integrada ao mesmo painel do crediário: assim que uma parcela vence, a Fidexa dispara a mensagem pelo WhatsApp automaticamente, sem precisar exportar dados para outra ferramenta ou disparar manualmente.",
  },
  {
    question:
      "O sistema de crediário da Fidexa é seguro e está de acordo com a LGPD?",
    answer:
      "Sim. A Fidexa segue a LGPD no tratamento dos dados dos seus clientes e utiliza a API oficial do WhatsApp (Meta), com acesso controlado por loja e dados protegidos em toda a operação de crediário.",
  },
];

const STEPS = [
  {
    title: "Cadastre sua base",
    desc: "Importe clientes, parcelas e vencimentos por planilha — sem precisar trocar de sistema.",
  },
  {
    title: "Configure o crediário",
    desc: "Defina limites, condições de pagamento e a régua de cobrança de cada etapa do vencimento.",
  },
  {
    title: "Acompanhe as parcelas",
    desc: "Veja em tempo real quem está em dia e quem está inadimplente, loja por loja.",
  },
  {
    title: "Cobre automaticamente",
    desc: "A Fidexa dispara as mensagens de cobrança pelo WhatsApp no momento certo, sem esforço manual.",
  },
];

export const Crediario = () => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const openDemo = (label: string) => {
    trackEvent({ event: "cta_click_hero", cta_label: label });
    setIsDemoOpen(true);
  };

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Olá! Quero saber mais sobre o sistema de crediário da Fidexa.",
  )}`;

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta
          name="keywords"
          content="sistema de crediário, sistema de crediário próprio, crediário para rede de lojas, gestão de crediário, controle de inadimplência"
        />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(softwareApplicationSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen max-w-screen overflow-hidden bg-white">
        <Header />

        <main className="flex flex-col gap-20">
          {/* Hero */}
          <section
            aria-labelledby="hero-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <div className="flex min-h-screen items-center pt-24 pb-12 lg:pt-28">
              <div className="grid w-full items-center gap-10 lg:grid-cols-2 lg:gap-12">
                <div className="flex flex-col gap-6 text-left lg:gap-8">
                  <motion.h1
                    id="hero-title"
                    className="text-title-xl leading-[1.08] font-bold text-black"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  >
                    Sistema de crediário para redes de lojas
                  </motion.h1>

                  <motion.p
                    className="max-w-[480px] text-base text-gray-700"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  >
                    A Fidexa é o sistema de crediário próprio da sua rede de
                    lojas: cadastro de clientes, controle de parcelas e
                    cobrança automática via WhatsApp em um só painel.
                  </motion.p>

                  <motion.div
                    className="flex flex-col gap-3 sm:flex-row"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                  >
                    <Button
                      className="h-12 w-auto rounded-full px-7 text-base"
                      onClick={() => openDemo("Quero meu sistema de crediário")}
                    >
                      Quero meu sistema de crediário
                    </Button>
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackEvent({
                          event: "cta_click_whatsapp",
                          location: "hero",
                        })
                      }
                    >
                      <Button
                        variant="ghost"
                        className="h-12 w-auto rounded-full border border-black/10 bg-gray-100 px-7 text-base font-medium text-black hover:bg-gray-200"
                      >
                        Falar no WhatsApp
                      </Button>
                    </a>
                  </motion.div>
                </div>

                <motion.div
                  className="flex justify-center lg:justify-end"
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  <img
                    src="/images/header-fidexa.png"
                    alt="Print do chat de cobrança da Fidexa no WhatsApp, com a régua de cobrança enviando mensagem automática ao cliente"
                    width={748}
                    height={735}
                    className="h-auto w-full max-w-md"
                    fetchPriority="high"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefícios */}
          <section
            aria-labelledby="beneficios-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <h2
              id="beneficios-title"
              className="mb-10 text-3xl font-bold text-black sm:text-4xl lg:text-5xl"
              style={MERRIWEATHER}
            >
              Por que ter um sistema de crediário próprio
            </h2>

            <ul className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {BENEFITS.map(benefit => (
                <li key={benefit} className="flex items-start gap-3">
                  <CheckCircle2 className="text-primary mt-0.5 size-6 shrink-0" />
                  <span className="text-lg text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Como funciona */}
          <section
            aria-labelledby="como-funciona-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <h2
              id="como-funciona-title"
              className="mb-10 text-3xl font-bold text-black sm:text-4xl lg:text-5xl"
              style={MERRIWEATHER}
            >
              Como funciona o sistema de crediário da Fidexa
            </h2>

            <ol className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className="flex flex-col gap-3 rounded-2xl bg-gray-100 p-6"
                >
                  <span className="bg-primary flex size-10 items-center justify-center rounded-full text-lg font-bold text-white">
                    {index + 1}
                  </span>
                  <h3 className="text-lg font-bold text-black">{step.title}</h3>
                  <p className="text-base text-gray-700">{step.desc}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* Diferencial rede de lojas */}
          <section
            aria-labelledby="rede-title"
            className="mx-auto w-full max-w-6xl px-4 sm:px-0"
          >
            <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <img
                src={chatFidexaImg}
                alt="Painel da Fidexa mostrando várias lojas da rede com as conversas de cobrança via WhatsApp centralizadas"
                width={1360}
                height={824}
                className="h-auto w-full"
                loading="lazy"
              />

              <div className="flex flex-col gap-5">
                <h2
                  id="rede-title"
                  className="text-3xl font-bold text-black sm:text-4xl"
                  style={MERRIWEATHER}
                >
                  Feito para redes de 4 a 20 lojas
                </h2>
                <p className="text-lg text-gray-700">
                  Gerenciar o crediário de várias unidades em planilhas e
                  cadernos separados é caro e arriscado. A Fidexa centraliza o{" "}
                  <strong>sistema de crediário de todas as lojas</strong> em um
                  só painel, com visão por unidade e padronização das regras de
                  crédito em toda a rede.
                </p>
                <p className="text-lg text-gray-700">
                  E como a cobrança já sai direto pelo WhatsApp, é a forma mais
                  simples de reduzir a inadimplência sem aumentar o time
                  financeiro.{" "}
                  <Link
                    to="/cobranca-whatsapp"
                    className="text-primary font-semibold underline underline-offset-2"
                  >
                    Veja como funciona a cobrança via WhatsApp
                  </Link>
                  .
                </p>
                <p className="text-lg text-gray-700">
                  Quer entender a régua completa, do lembrete antes do
                  vencimento até a cobrança automática do atraso?{" "}
                  <Link
                    to="/lojistas-que-vendem-no-crediario-proprio"
                    className="text-primary font-semibold underline underline-offset-2"
                  >
                    Conheça a régua de cobrança automatizada da Fidexa
                  </Link>
                  .
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="px-4 sm:px-0">
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col items-center gap-6 rounded-[2rem] bg-black px-6 py-16 text-center sm:py-20">
                <h2
                  className="max-w-2xl text-3xl font-bold text-white sm:text-4xl"
                  style={MERRIWEATHER}
                >
                  Pronto para ter o crediário da sua rede em um só lugar?
                </h2>
                <p className="max-w-xl text-lg text-gray-300">
                  Agende uma demonstração gratuita e veja o sistema de
                  crediário da Fidexa funcionando na prática.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    className="h-12 w-auto rounded-full px-7 text-base"
                    onClick={() => openDemo("Agendar demonstração")}
                  >
                    Agendar demonstração
                  </Button>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent({
                        event: "cta_click_whatsapp",
                        location: "footer",
                      })
                    }
                  >
                    <Button
                      variant="ghost"
                      className="h-12 w-auto rounded-full bg-white/10 px-7 text-base font-medium text-white hover:bg-white/20"
                    >
                      Falar no WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ específica de sistema de crediário (evita FAQPage duplicado
              com a home e /cobranca-whatsapp, que usam o conjunto genérico) */}
          <section className="px-4 sm:px-0">
            <div className="mx-auto max-w-6xl">
              <FAQ items={FAQ_ITEMS} />
            </div>
          </section>

          <Footer />
        </main>

        <WhatsAppFloatButton />
        <DemoFormModal isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
      </div>
    </>
  );
};
